import { createClient, SupabaseClient } from '@supabase/supabase-js';
import logger from './logger';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Create a dummy client or null - don't throw at module load time
let supabase: SupabaseClient | null = null;

try {
  // Only validate if we have actual values (not placeholders)
  const hasValidUrl = supabaseUrl && 
    supabaseUrl !== 'https://your-project-id.supabase.co' && 
    supabaseUrl.startsWith('http');
  const hasValidKey = supabaseAnonKey && 
    supabaseAnonKey !== 'your-anon-key-here' && 
    supabaseAnonKey.length > 20;

  if (hasValidUrl && hasValidKey) {
    // Säkerhetskontroll: service_role får ALDRIG användas i frontend
    if (supabaseAnonKey.includes('service_role')) {
      logger.security('Förbjuden nyckel upptäckt i frontend-konfiguration', { keyType: 'service_role' });
      throw new Error('service_role key får inte användas i frontend!');
    }

    logger.info('Supabase client initieras', { 
      url: supabaseUrl.replace(/https?:\/\/([^.]+).*/, 'https://$1.redacted'),
      hasAnonKey: true 
    });

    // CRITICAL FIX: Use localStorage instead of sessionStorage for session persistence
    // sessionStorage clears on page refresh, localStorage persists across refreshes
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false, // Set to false to prevent URL token detection race condition
        storageKey: 'caseflow-auth',
        storage: {
          getItem: (key: string) => {
            const value = localStorage.getItem(key);
            
            // DIAGNOSTICS: Log storage read
            if (key === 'caseflow-auth') {
              console.log('SUPABASE_STORAGE_GET', {
                key,
                hasValue: !!value,
                valueLength: value?.length || 0,
                isRawString: typeof value === 'string'
              });
            }
            
            // CRITICAL FIX: Return raw string exactly as stored
            // Supabase auth storage interface expects: getItem(key) => string | null
            // Supabase client internally calls JSON.parse() on the returned value
            // Do NOT JSON.parse here - return raw string
            return value;
          },
          setItem: (key: string, value: string) => {
            // DIAGNOSTICS: Log storage write
            if (key === 'caseflow-auth') {
              try {
                const parsed = JSON.parse(value);
                console.log('SUPABASE_STORAGE_SET', {
                  key,
                  hasUser: !!parsed?.user,
                  hasSession: !!parsed?.session,
                  userId: parsed?.user?.id?.substring(0, 8) + '...' || 'none',
                  userAud: parsed?.user?.aud || 'none'
                });
                
                logger.audit('AUTH_SESSION_STORED', {
                  hasUser: !!parsed?.user,
                  hasSession: !!parsed?.session,
                  userId: parsed?.user?.id ? parsed.user.id.substring(0, 8) + '...' : 'none'
                  // INTENTIONALLY NOT logging email or tokens for security
                });
              } catch (e) {
                console.log('SUPABASE_STORAGE_SET_PARSE_ERROR', { key, error: String(e) });
              }
            }
            
            localStorage.setItem(key, value);
          },
          removeItem: (key: string) => {
            // DIAGNOSTICS: Log storage removal
            if (key === 'caseflow-auth') {
              console.log('SUPABASE_STORAGE_REMOVE', { key });
            }
            localStorage.removeItem(key);
          },
        },
      },
    });
  } else {
    logger.warn('Supabase konfiguration saknas eller ogiltig - kör utan backend', {
      hasUrl: hasValidUrl,
      hasKey: hasValidKey,
      url: supabaseUrl?.substring(0, 20) + '...'
    });
  }
} catch (error) {
  logger.error('Fel vid initiering av Supabase client', { error });
}

// Export a mock or real client
export const getSupabase = () => {
  if (!supabase) {
    throw new Error('Supabase är inte konfigurerat. Kontrollera VITE_SUPABASE_URL och VITE_SUPABASE_ANON_KEY i .env.local');
  }
  return supabase;
};

// For backward compatibility, but will be null if not configured
export { supabase };
export type AuthUserWithProfile = any; // Simplified for now
